
import axios from 'axios';
import { getToken, clearSession } from './authApi';

// Production API URL from Vite environment variable
const API_URL =
  import.meta.env.VITE_API_URL || 'https://dicom.s3dwebtech.in';

const client = axios.create({
  baseURL: `${API_URL}/api/dicom`,
});

// Add authentication token to API requests
client.interceptors.request.use(
  (config) => {
    const token = getToken();

    if (token) {
      config.headers = config.headers || {};
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error)
);

// Handle authentication errors
client.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err.response?.status === 401) {
      clearSession();

      // Send user back to login screen
      window.location.reload();
    }

    return Promise.reject(err);
  }
);

// Upload DICOM files
export const uploadFiles = (fileList) => {
  const form = new FormData();

  Array.from(fileList).forEach((file) => {
    form.append('files', file);
  });

  return client.post('/upload', form);
};

// List DICOM files
export const listFiles = (params) => {
  return client.get('/', { params });
};

// Get single DICOM file
export const getFile = (id) => {
  return client.get(`/${encodeURIComponent(id)}`);
};

// Update DICOM file
export const updateFile = (id, fields) => {
  return client.put(`/${encodeURIComponent(id)}`, fields);
};

// Bulk update DICOM files
export const bulkUpdate = (ids, fields) => {
  return client.put('/bulk', {
    ids,
    fields,
  });
};

// Anonymize DICOM files
export const anonymizeFiles = (ids) => {
  return client.post('/anonymize', {
    ids,
  });
};

// Delete DICOM file
export const deleteFile = (id) => {
  return client.delete(`/${encodeURIComponent(id)}`);
};

// Preview URL
// <img> and <a> tags cannot send Authorization headers,
// so the token is passed as a query parameter.
export const previewUrl = (id, params = {}) => {
  const queryParams = new URLSearchParams({
    ...params,
    token: getToken() || '',
  });

  return `${API_URL}/api/dicom/${encodeURIComponent(id)}/preview?${queryParams.toString()}`;
};

// Get DICOM window information
export const getWindowInfo = (id) => {
  return client.get(`/${encodeURIComponent(id)}/window-info`);
};

// Download URL
// Token is passed as a query parameter because <a> tags
// cannot send Authorization headers.
export const downloadUrl = (id) => {
  const queryParams = new URLSearchParams({
    token: getToken() || '',
  });

  return `${API_URL}/api/dicom/${encodeURIComponent(id)}/download?${queryParams.toString()}`;
};

export default client;

